import { Selection, EnterElement, select } from "d3"
import { D3OneToManyRenderable } from "../../../D3/D3OneToManyRenderable"
import { RenderStrategy, SolidColorTraceDataConfig, TraceConfig, TraceDataConfig } from "../../../../Types/Trace"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import React from "react"
import { isGenericAnalytic } from "../../../../Types/AnalysisDetails"

type D3LegendLabelsConfig = {
	traces: SolidColorTraceDataConfig[]
	graphId: string
	isClickDisabled?: boolean
}

export class D3LegendLabels extends D3OneToManyRenderable<HTMLDivElement, D3LegendLabelsConfig, SolidColorTraceDataConfig, ModalityGraphGroupReactCallbacks> {

	constructor(root: HTMLDivElement, config: D3LegendLabelsConfig, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, "d3-legend-labels", reactCallbacks)
		this.mount()
	}

	protected datumIdentifier(datum: SolidColorTraceDataConfig): string | number {
		return `${datum.name}-${datum.color}`
	}

	protected getConfigs(): SolidColorTraceDataConfig[] {
		return this.config.traces.filter(trace => trace.name !== undefined)
	}

	protected enter(newElements: Selection<EnterElement, SolidColorTraceDataConfig, Element, any>): Selection<HTMLDivElement, SolidColorTraceDataConfig, Element, any> {
		const elements = newElements
			.append("div")
			.attr("class", this.className)
			.style("cursor", trace => this.getCursor(trace))
			.attr("title", trace => this.traceFoundInRecording(trace as TraceConfig) ? "" : `${trace.rawDataKey} was not found in this recording.`)
			.style("color", trace => trace.color)
			.style("-webkit-text-fill-color", trace => trace.color ) // for screenshots
			.style("opacity", trace => (trace.isVisible ?? true) && this.traceFoundInRecording(trace as TraceConfig) ? 1 : 0.2)
			.text(trace => trace.name)

		if (!this.config.isClickDisabled) {
			elements.each((config, index, nodes) => this.attachListeners(config, index, nodes))
		}

		return elements
	}

	protected update(updatedElements: Selection<HTMLDivElement, SolidColorTraceDataConfig, Element, any>): Selection<HTMLDivElement, SolidColorTraceDataConfig, Element, any> {
		return updatedElements
			.style("color", trace => trace.color)
			.style("-webkit-text-fill-color", trace => trace.color ) // for screenshots
			.style("opacity", trace => (trace.isVisible ?? true) && this.traceFoundInRecording(trace as TraceConfig) ? 1 : 0.2)
			.text(trace => trace.name)
	}

	private traceFoundInRecording = (trace: TraceConfig) => {
		if (trace.renderStrategy === RenderStrategy.DELTA) {
			return this.dataConfigFound(trace.first) && this.dataConfigFound(trace.second)
		}

		return this.dataConfigFound(trace)
	}

	private getCursor(trace: TraceDataConfig): string {
		if (this.config.isClickDisabled) {
			return "default"
		}

		if (this.traceFoundInRecording(trace as TraceConfig)) {
			return "pointer"
		}

		return "not-allowed"
	}

	private dataConfigFound = (trace: TraceDataConfig) => (
		this.reactCallbacks.patientModalities.includes(trace.rawDataKey) ||
		(trace.onDemandAnalysis?.analytic !== undefined && !isGenericAnalytic(trace.onDemandAnalysis?.analytic))
	)

	private attachListeners = (config: SolidColorTraceDataConfig, index: number, nodes: ArrayLike<HTMLDivElement>) => {
		const selection = select(nodes[index]) as Selection<any, SolidColorTraceDataConfig, any, any>

		if (!this.traceFoundInRecording(config as TraceConfig)) {
			return
		}

		selection
			.on("mousedown", (event: React.MouseEvent, config) => {
				if (event.buttons === 1) {
					this.reactCallbacks.toggleTraceVisibility(this.config.graphId, config.id)
				}
			})
			.on("contextmenu", (event: React.MouseEvent, config) => {
				event.preventDefault()
				this.reactCallbacks.openTraceVisibilityContextMenu(event, config, this.config.graphId)
			})
	}
}
